import React, { useCallback, useEffect, useMemo } from 'react';
import { useState } from 'react';
import { useLogger, useReceiptGenerator, useSelector } from '~Hooks';
import { Refund } from './types';
import { RefundMethodsController } from './Controller';

interface Context {
  hasRefundInProgress: boolean;
  add: (refund: Refund) => void;
}

const Context = React.createContext<Context>({} as any);

/**
 * The Refund Provider allows us to initiate refunds from
 * anywhere in the app and keeps track of their lifecycle.
 */
const RefundsProvier: React.FC = ({ children }) => {
  const venueId = useSelector((state) => state.venueId!);
  const generateReceipt = useReceiptGenerator({
    venueId,
  });

  const [refunds, setRefunds] = useState<Refund[]>([]);
  const logger = useLogger('refunds');

  const mountLogger = useLogger('mount');

  useEffect(() => {
    mountLogger.info('Mounted: RefundsProvier');
    return () => {
      mountLogger.info('Unmounted: RefundsProvier');
    };
  }, []);

  /**
   * Adds a refund to the list of refunds in progress
   */
  const addRefund = useCallback((refund: Refund) => {
    logger.info('Initiate refund', { refund });

    if (refund.type !== 'TIP' && !refund.items.length) {
      logger.error('Invalid Refund - Missing ItemIDs', { refund });
      refund.onFail('Invalid Refund - Missing ItemIDs');

      return;
    }

    // Refunds are rendered keyed by transactionId: a leftover entry for the same payment
    // would swallow the new one, so nothing happens. The new attempt replaces it.
    setRefunds((r) => {
      if (r.some((existing) => existing.transactionId === refund.transactionId)) {
        logger.warn('Replacing a refund still in progress for the same payment', {
          transactionId: refund.transactionId,
        });
      }
      return [...r.filter((existing) => existing.transactionId !== refund.transactionId), refund];
    });
  }, []);

  const removeRefund = useCallback((transactionId: string) => {
    setRefunds((r) => r.filter((refund) => refund.transactionId !== transactionId));
  }, []);

  /**
   * Memoized so that references dont change on every render
   */
  const value: Context = useMemo(
    () => ({
      add: addRefund,
      hasRefundInProgress: refunds.length > 0,
    }),
    [addRefund, refunds],
  );

  return (
    <Context.Provider value={value}>
      {/* Render all refunds in progress */}
      {refunds.map((refund) => {
        return (
          <RefundMethodsController
            {...refund}
            onFinished={removeRefund}
            generateReceipt={generateReceipt}
            key={refund.transactionId}
          />
        );
      })}

      {children}
    </Context.Provider>
  );
};

export const useRefunds = () => {
  return React.useContext(Context);
};

export default RefundsProvier;
